import { AxesProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axes';
import ChartTitleProperty from '@src/slide/domain/ui-model/element-modeles/property/chart/title';
import {
    createSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/factory';
import { LegendProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/legend';
import forEach from 'lodash/forEach';
import { transaction } from '@src/slide/domain/slide-app';
import { ChartModelFactory } from '../../chart-models/chart-presets/chart-model-factory';
import type { IDataLabels } from '../../Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import type { IChartModel } from '../../Interface/chart-v3/chart-model.interface';
import type { ILegendProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/legend/legend.interface';
import type { IChartTitleProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/title/interface';
import type {
    IChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/property-modules.interface';
import type { ChartContext } from '@src/slide/domain/ui-model/element-modeles/chart-models/context';
import type {
    IBaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart.interface';
import type { IAxesProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axes.interface';
import type { IChartLegend, IChartTitle } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type { Nullable } from './chart-shape-property.interface';
import {
    ChartElementType,
    IChartElement,
    IChartElementGroup,
    SelectedElement,
    SpecialGeometryType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import { IStockChartProperty } from './sub-chart/stock-chart.interface';
import { girdlinesTypeMapping } from '../../constant/chart';
import { isMarkerAvailableLine } from './util/geometry';
import {
    IChartSpaceProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/chart-space/chart-space.interface';
import { ChartSpaceProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/chart-space/chart-space';
import { ISubChartProperty } from './sub-chart';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import {
    IDataLabelProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label/data-label.interface';
import { IChartActionType } from '@src/slide/domain/ui-model/element-modeles/property/chart/constant';

/*
 * PropertyModules是一些图表模块Property的集合， 为UI层服务，
 * 这些Property，基于原本的model，服务于UI层，提供了：
 *   1.相应模块的编辑接口，如：更新标题、更新子表数据等
 *   2.提供了业务通用的数据处理，如：坐标轴的单位计算、最终Slide图表合法业务数据的兼容、数据标签通过字段解析出文本内容等
 *
 * 这些Property是业务层通用的，比如侧边栏、图表的Config，都直接面向Property进行数据获取。
 * 如果有各自独立的数据逻辑，就基于Property的数据，再进行处理
 */
export class ChartProperty implements IChartProperty {
    public chartTitle?: IChartTitleProperty;
    public axes: IAxesProperty;
    public subCharts: IBaseSubChartProperty[];
    public legend: ILegendProperty;
    public chartSpace: IChartSpaceProperty;

    public constructor(private context: ChartContext) {}

    public init() {
        /* 初始化标题模块 */
        this.initTitle();

        /*  初始化坐标轴属性，不包含坐标轴的values和一些如单位的属性。这些需要在子表初始化完之后，依赖左右的子表属性进行计算 */
        this.initAxes();

        /* 初始化子表模块，并创建属于子表的context，一并set到subChartModel上 */
        this.initSubCharts();

        /* 初始化坐标轴的values和一些如"单位"的属性 */
        this.axes.initAxisValues();

        /* 初始化图例内容，结果为最后的图例列表 */
        this.initLegend();

        this.initChartSpace();
        return this;
    }

    public createTitle(props?: Partial<IChartTitle>): void {
        this.chartModel.updateProps({ title: ChartModelFactory.createTitle(props) });
    }

    public removeTitle(): void {
        this.chartTitle?.removeTitle();
    }

    public createLegend(props?: Partial<IChartLegend>): void {
        this.chartModel.updateProps({ legend: ChartModelFactory.createLegend(undefined, undefined, props) });
    }

    public removeLegend(): void {
        this.legend.removeLegend();
    }

    public updateDataLabels(props?: Partial<Nullable<IDataLabels>>): void {
        transaction(() => {
            forEach(this.subCharts, subChart => subChart.updateDataLabels(props));
        });
    }

    public removeDataLabels(): void {
        transaction(() => {
            forEach(this.subCharts, subChart => subChart.removeDataLabels());
        });
    }

    public createAllAxes(): void {
        this.axes.showAllAxes();
    }

    public removeAllAxes(): void {
        this.axes.hideAllAxes();
    }

    public createAllAxesTitle(): void {
        this.axes.createAllAxesTitle();
    }

    public removeAllAxesTitle(): void {
        this.axes.removeAllAxesTitle();
    }

    public createAllAxesGridlines(): void {
        this.axes.createAllAxesGridlines();
    }

    public removeAllAxesGridlines(): void {
        this.axes.removeAllAxesGridlines();
    }

    public removeTrendlines(): void {
        transaction(() => {
            forEach(this.subCharts, subChart => subChart.removeTrendlines());
        });
    }

    // eslint-disable-next-line complexity
    public getSelectedElementProperty(selectedElementType: ChartElementType, showMarker?: boolean) {
        switch (selectedElementType) {
            case ChartElementType.TITLE: {
                return this.chartTitle;
            }
            case ChartElementType.LEGEND: {
                return this.legend;
            }
            case ChartElementType.AXIS_LABEL: {
                const axis = this.getSelectedAxis();
                return axis || null;
            }
            case ChartElementType.AXIS_TITLE: {
                const axis = this.getSelectedAxis();
                return axis?.title || null;
            }
            case ChartElementType.DATA_LABEL: {
                const dataLabel = this.getSelectedDataLabel();
                return dataLabel || null;
            }
            case ChartElementType.MAJOR_GRID:
            case ChartElementType.MINOR_GRID: {
                const axis = this.getSelectedAxis();
                const selectedGridLinesType = girdlinesTypeMapping[selectedElementType];
                const girdlines = axis?.[selectedGridLinesType];
                return girdlines || null;
            }
            case ChartElementType.GEOMETRY: {
                const geometry = this.getSelectedGeometry(showMarker);
                return geometry || null;
            }
            default:
                return null;
        }
    }
    // 获取ChartSpaceProperty
    public getChartSpaceProperty() {
        return this.chartSpace;
    }
    // 获取当前选中数据项所在的subChartProperty
    public getSelectedSubChartProperty(): ISubChartProperty | undefined {
        const { selectedElement } = this.chartModel;
        if (!selectedElement) return;
        if (this.selectedElementHasGroup(selectedElement)) {
            const { extra = {} } = selectedElement.group[0];
            const { seriesId, specialGeometryType, chartId  } = extra;
            if (specialGeometryType === SpecialGeometryType.highLowLine) {
                const subChart = this.subCharts.find(subChart => subChart.id === chartId) as IStockChartProperty;
                return subChart;
            }
            if (specialGeometryType === SpecialGeometryType.upDownBar) {
                const subChart = this.subCharts.find(subChart => subChart.id === chartId) as IStockChartProperty;
                return subChart;
            }
            const subChart = this.getSubChartBySeries(seriesId);
            return subChart;
        }
        const { extra = {} } = selectedElement ;
        const { seriesId } = extra;
        const subChart = this.getSubChartBySeries(seriesId);
        return subChart;
    }
    /**
     * 更新图表的主题色盘
     * @param chartColor
     */
    public updateChartColors(chartColor) {
        transaction(() => {
            this.chartModel.updateProps({ chartSpace: { colorStyle: chartColor } });
            this.updateChartColorsWithTheme(chartColor);
        });
    }

    /**
     * 根据图表主题色盘更新图表图形的颜色
     */
    public updateChartColorsWithTheme(chartColor) {
        transaction(() => {
            forEach(this.subCharts, subChart => subChart.updateChartColorsWithTheme(chartColor));
        });
    }

    /**
     * 更新元素的的自定义布局
     * 目前的使用场景只是title，其他元素都是走图表库的drag事件，然后直接调用的property
     */
    public updateSelectElementLayout(rect: IRect, diff: IRect) {
        const elementType = this.chartModel.selectedElement?.type;
        if (elementType === ChartElementType.LEGEND) {
            this.updateLegendLayout(rect, diff);
        } else if (elementType === ChartElementType.DATA_LABEL) {
            this.updateDataLabelLayout(rect, diff);
        } else if (elementType === ChartElementType.TITLE) {
            this.updateTitleLayout(rect);
        }
    }

    /**
     * 更新编辑中的文本样式
     */
    updateEditingSelectTextStyle(action: IChartActionType, values) {
        const { selectedElement } = this.chartModel;
        // 目前只有title支持富文本的编辑
        if ((selectedElement as IChartElement)?.type === ChartElementType.TITLE) {
            this.chartTitle?.updateTitleStyle(action, values);
        }
    }

    private selectedElementHasGroup(selectedElement: SelectedElement): selectedElement is IChartElementGroup {
        return 'group' in selectedElement;
    }

    /**
     * 更新标题布局
     */
    private updateTitleLayout(rect: IRect) {
        const { props } = this.chartModel;
        this.chartTitle?.updateLayout({
            left: rect.left / props.style.width,
            top: rect.top / props.style.height,
        });
    }

    /**
     * 更新图例布局
     */
    private updateLegendLayout(rect: IRect, diff: IRect) {
        const { props } = this.chartModel;
        // 图例的layout大部分是边缘模式
        this.legend?.updateLayout({
            left: rect.left / props.style.width,
            top: rect.top / props.style.height,
            width: diff.width ? this.limitIn(rect.width / props.style.width, 1) : undefined,
            height: diff.height ? this.limitIn(rect.height / props.style.height, 1) : undefined,
        });
    }

    /**
     * 更新数据标签布局
     */
    private updateDataLabelLayout(rect: IRect, diff: IRect) {
        const { chartProperty, props } = this.chartModel;
        const dataLabelProperty = chartProperty
            .getSelectedElementProperty(ChartElementType.DATA_LABEL) as IDataLabelProperty;

        const isResize = diff.width || diff.height;

        // 在resize下，只变化大小，不变化位置
        // 原因：因为数据标签根据position，会在不同图形下做不同的调整偏移策略
        // 如果要实现数据标签为resize之后的位置，需要根据不同图形的不同position的偏移规则，反向的计算偏移补偿，这部分非常繁琐和复杂
        // office也没有实现这种效果，所以这里目前只做大小变化。如果需要位置的变化，可以通过拖拽来实现
        const layoutPatch = isResize ? {
            width: diff.width ? this.limitIn(rect.width / props.style.width, 1) : undefined,
            height: diff.height ? this.limitIn(rect.height / props.style.height, 1) : undefined,
        } : {
            left: diff.left ? diff.left / props.style.width : undefined,
            top: diff.top ? diff.top / props.style.height : undefined,
        };

        dataLabelProperty?.updateLayout(layoutPatch);
    }

    private initChartSpace() {
        const chartModel = this.context.getChartModel();
        this.chartSpace = new ChartSpaceProperty({
            props: chartModel.chartSpace?.props,
        }, this.context).init(chartModel.chartSpace);
    }

    private initAxes() {
        const chartModel = this.context.getChartModel();

        this.axes = new AxesProperty({
            categoryAxes: chartModel.plotArea.categoryAxes,
            valueAxes: chartModel.plotArea.valueAxes,
            dateAxes: chartModel.plotArea.dateAxes,
        }, this.context).init();
    }

    private initSubCharts() {
        const chartModel = this.context.getChartModel();

        this.subCharts = chartModel.plotArea.charts.map((subChart) => {
            const context = this.context.bindSubChart(subChart);
            /* 将绑定子表的context设置到子表上 */
            subChart.setContext(context);
            /* 将subChartProperty设置到子表上 */
            const subChartProperty = createSubChartProperty(subChart);
            if (subChartProperty) {
                subChart.setSubChartProperty(subChartProperty);
                subChartProperty.init();
            }

            return subChart.getSubChartProperty();
        });
    }

    private initTitle() {
        const chartModel = this.context.getChartModel();
        if (chartModel.title) {
            this.chartTitle = new ChartTitleProperty(chartModel.title, this.context);
        }
    }

    private initLegend() {
        const chartModel = this.context.getChartModel();
        if (chartModel.legend) {
            this.legend = new LegendProperty(chartModel.legend, this.context).init();
        }
    }

    private get chartModel(): IChartModel {
        return this.context.getChartModel();
    }

    private getSelectedAxis() {
        const { selectedElement } = this.chartModel;
        if (!selectedElement) return;
        const selectedAxisId = 'group' in selectedElement
            ? selectedElement.group[0]?.extra?.axisId : selectedElement?.extra?.axisId;
        if (selectedAxisId) {
            const axis = this.axes.getRelativeAxisById(selectedAxisId);
            return axis;
        }
    }

    private getSelectedDataLabel() {
        const { selectedElement } = this.chartModel;
        if (!selectedElement) return;
        // 有group表示选中的是整个系列的dataLabel
        if ('group' in selectedElement) {
            const { seriesId } = selectedElement.group[0].extra || {};
            const subChart = this.getSubChartBySeries(seriesId);
            const series = subChart?.getSeries(seriesId);
            const dataLabels =  series?.getDataLabelsProperty();
            return dataLabels;
        }
        const { seriesId, dataId } = selectedElement?.extra || {};
        const subChart = this.getSubChartBySeries(seriesId);
        const dataPoint = subChart?.getDataItem(dataId);
        const dataLabel =  dataPoint?.label;
        return dataLabel;
    }

    private getSelectedGeometry(showMarker?: boolean) {
        const { selectedElement } = this.chartModel;
        if (!selectedElement) return;
        // 有group表示选中的是整个系列
        if ('group' in selectedElement) {
            const { extra = {}, shapeType } = selectedElement.group[0];
            const { seriesId, specialGeometryType, chartId, data } = extra;
            if (specialGeometryType === SpecialGeometryType.highLowLine) {
                const subChart = this.subCharts.find(subChart => subChart.id === chartId) as IStockChartProperty;
                return subChart?.getHighLowLines();
            }
            if (specialGeometryType === SpecialGeometryType.upDownBar) {
                const subChart = this.subCharts.find(subChart => subChart.id === chartId) as IStockChartProperty;
                return subChart?.getUpDownBars(data.barType);
            }
            const subChart = this.getSubChartBySeries(seriesId);
            const series = subChart?.getSeries(seriesId);
            const geometry = series?.getGeometryProperty(!!(isMarkerAvailableLine(shapeType) && showMarker));
            return geometry;
        }
        const { extra = {}, shapeType } = selectedElement;
        const { seriesId, dataId, specialGeometryType, data  } = extra;
        const subChart = this.getSubChartBySeries(seriesId);
        if (!subChart) return;
        // 获取趋势线
        if (specialGeometryType === SpecialGeometryType.trendLine) {
            return subChart.getTrendline(seriesId, data[0].trendlineIndex);
        }
        const dataPoint = subChart?.getDataItem(dataId);
        const geometry = dataPoint?.getGeometryProperty(!!(isMarkerAvailableLine(shapeType) && showMarker));
        return geometry;
    }

    private getSubChartBySeries(seriesId: string): IBaseSubChartProperty | undefined {
        const subChart = this.subCharts.find(subChart => subChart.series.some(ser => ser.seriesId === seriesId));
        return subChart;
    }

    private limitIn(limit: number, value: number) {
        return Math.min(Math.max(value, 0), limit);
    }
}
